/**
 * React Query hooks for WT-5a (fieldiq-api#1254): invoices from
 * customer-approved field tickets — the Pipeline & Facility hooks
 * (usePfInvoices.ts) carried over, without mark-paid (WT-5b).
 *
 * Same rules as useWellTesting.ts: every key starts ['wellTesting'] and every
 * write invalidates that one prefix (an invoice moves the register, the
 * billable list, the job's invoices and the ticket's invoice line); every read
 * spreads LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a refusal, never as
 * an empty list.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateWt, unwrapWt, unwrapWtList } from './useWellTesting';
import type { WtBillableGroup, WtInvoice, WtInvoiceGenerate, WtInvoiceRecipient, WtInvoiceRow, WtInvoiceVoided } from '../types/wt-invoices';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const wtInvoiceKeys = {
  invoices: (p?: Params) => ['wellTesting', 'invoices', clean(p) ?? {}] as const,
  billable: () => ['wellTesting', 'invoicesBillable'] as const,
  invoice: (id: string) => ['wellTesting', 'invoice', id] as const,
  recipient: (id: string) => ['wellTesting', 'invoiceRecipient', id] as const,
};

function useWtInvoiceWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWt(qc) });
}

export interface WtInvoiceFilters extends Params {
  status?: string;
  customer_id?: string;
  job_id?: string;
}

export function useWtInvoices(filters?: WtInvoiceFilters, enabled = true) {
  return useQuery({
    queryKey: wtInvoiceKeys.invoices(filters),
    queryFn: async () => unwrapWtList<WtInvoiceRow>((await api.wellTesting.invoices.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtBillable(enabled = true) {
  return useQuery({
    queryKey: wtInvoiceKeys.billable(),
    queryFn: async () => unwrapWtList<WtBillableGroup>((await api.wellTesting.invoices.billable()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtInvoice(id: string | undefined) {
  return useQuery({
    queryKey: wtInvoiceKeys.invoice(id ?? ''),
    queryFn: async () => unwrapWt<WtInvoice>((await api.wellTesting.invoices.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Several invoices' details at once — the ticket page asks which live invoice holds a ticket. */
export function useWtInvoiceDetails(ids: string[], enabled = true) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: wtInvoiceKeys.invoice(id),
      queryFn: async () => unwrapWt<WtInvoice>((await api.wellTesting.invoices.get(id)).data),
      enabled,
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
}

/** Who a send with no `to` would address — the office's write role only (the API gates it as a write). */
export function useWtInvoiceRecipient(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wtInvoiceKeys.recipient(id ?? ''),
    queryFn: async () => unwrapWt<WtInvoiceRecipient>((await api.wellTesting.invoices.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useGenerateWtInvoice = () =>
  useWtInvoiceWrite(async (body: WtInvoiceGenerate) => unwrapWt<WtInvoice>((await api.wellTesting.invoices.generate(body)).data).data);

export const useSendWtInvoice = () =>
  useWtInvoiceWrite(async ({ id, to, cc }: { id: string; to?: string; cc?: string[] }) => {
    const body: { to?: string; cc?: string[] } = {};
    if (to) body.to = to;
    if (cc && cc.length) body.cc = cc;
    return unwrapWt<WtInvoice & { sentToName?: string | null }>((await api.wellTesting.invoices.send(id, body)).data).data;
  });

/** Resolves to the voided invoice and the ids of the tickets it released (never dropped on the way). */
export const useVoidWtInvoice = () =>
  useWtInvoiceWrite(async ({ id, reason }: { id: string; reason: string }) => {
    const inv = unwrapWt<WtInvoiceVoided>((await api.wellTesting.invoices.void(id, reason)).data).data;
    return { invoice: inv, releasedTicketIds: Array.isArray(inv?.releasedTicketIds) ? inv!.releasedTicketIds : [] };
  });
